import { useCallback, useEffect, useId, useRef, useSyncExternalStore } from "react";
import {
  getSentenceSpeechState,
  speakSentence,
  stopSentenceSpeech,
  subscribeToSentenceSpeech,
} from "#sentence-speech-player";

/**
 * Press-driven Mandarin read-aloud for one sentence.
 *
 * Like `useGraphemePinyinAudio`, `toggle` is for `onPress` handlers only — never call it from an
 * effect, so a sentence never reads itself aloud on mount. `speak` is the one deliberate exception:
 * a line a character says as it appears (Han's Mandarin in a quest call) starts itself, and leaves
 * itself playing if it already is.
 *
 * `useId` gives each mounted button a stable identity for the app-wide playback, which is how
 * `isSpeaking`/`isPreparing` stay true for exactly the row that started playback while every
 * other row stays idle.
 */
export function useSentenceSpeech(
  sentence: string,
  options: {
    /** `false` speaks with the system voice instead of asking for one (see `speakSentence`). */
    canAskForVoice?: boolean;
    isMuted: boolean;
  },
): { isPreparing: boolean; isSpeaking: boolean; speak: () => void; toggle: () => void } {
  const { canAskForVoice = true, isMuted } = options;
  const ownerId = useId();

  const state = useSyncExternalStore(
    subscribeToSentenceSpeech,
    getSentenceSpeechState,
    // Server renders never speak, so playback is always idle there.
    () => null,
  );
  const isOwner = state?.ownerId === ownerId;
  const isSpeaking = isOwner && state.phase === "speaking";
  const isPreparing = isOwner && state.phase === "preparing";

  // Read the latest mute state without re-creating `toggle` — the player samples it at press
  // time (and again when synthesis finishes).
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const getIsMuted = useCallback(() => isMutedRef.current, []);

  // Muting mid-sentence should silence what is already playing, not just suppress the next press.
  useEffect(() => {
    if (isMuted && isOwner) {
      stopSentenceSpeech();
    }
  }, [isMuted, isOwner]);

  // A row scrolled out of a virtualized list (or a closed panel) must not leave a sentence
  // playing with no visible control to stop it. Only the owner stops: a row unmounting while a
  // *different* row plays must not cut it off.
  const isOwnerRef = useRef(isOwner);
  isOwnerRef.current = isOwner;
  useEffect(
    () => () => {
      if (isOwnerRef.current) {
        stopSentenceSpeech();
      }
    },
    [],
  );

  const toggle = useCallback(() => {
    speakSentence({ canAskForVoice, getIsMuted, ownerId, sentence });
  }, [canAskForVoice, getIsMuted, ownerId, sentence]);

  const speak = useCallback(() => {
    speakSentence({ canAskForVoice, getIsMuted, mode: "start", ownerId, sentence });
  }, [canAskForVoice, getIsMuted, ownerId, sentence]);

  return { isPreparing, isSpeaking, speak, toggle };
}
